import { Role, TenantUser, User } from '@cco/model';
import { auth2Actions } from '@core/auth2/store/auth2.actions';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';

export enum Auth2States {
  /*
    NOT_AUTHENTICATED = 'NOT_AUTHENTICATED',
    CHECKING_AUTH_STATUS = 'CHECKING_AUTH_STATUS',
    CHECK_AUTH_STATUS_TIMEDOUT = 'CHECK_AUTH_STATUS_TIMEDOUT',
    CHECK_AUTH_STATUS_FAILED = 'CHECKING_AUTH_STATUS_FAILED',
    */
  AUTHENTICATING = 'AUTHENTICATING',
  LOADING_USER_DATA = 'LOADING_USER_DATA',
  AUTHENTICATED = 'AUTHENTICATED',
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  // LOGGING_OUT = 'LOGGING_OUT',
  LOGGED_OUT = 'LOGGED_OUT',
}

export interface Auth2Errors {
  LOGIN_FAILURE: string;
}

export interface State {
  authenticated: boolean;
  state: Auth2States;
  previousState: Auth2States;
  currentTenantUser: TenantUser | undefined;
  currentUser: User;
  currentUserRoles: Role[] | undefined;
  redirectUrl: string;
}

export const initialState: State = {
  authenticated: false,
  state: Auth2States.LOGGED_OUT,
  previousState: null,
  currentTenantUser: undefined,
  currentUser: null,
  currentUserRoles: undefined,
  redirectUrl: null,
};

export const reducer = createReducer(initialState,
  on(auth2Actions.checkAuthenticationStatusSuccess, (state, { currentUser }): State => ({
    ...state,
    authenticated: true,
    state: Auth2States.AUTHENTICATED,
    previousState: state.state,
    currentUser,
    redirectUrl: null,
  })),
  on(auth2Actions.checkAuthenticationStatusFailure, (state): State => ({
    ...state,
    authenticated: false,
    state: Auth2States.AUTHENTICATION_FAILED,
    previousState: state.state,
  })),
  on(auth2Actions.authenticateUser, (state): State => ({
    ...state,
    authenticated: false,
    state: Auth2States.AUTHENTICATING,
    previousState: state.state,
  })),
  on(auth2Actions.authenticateUserSuccess, (state): State => ({
    ...state,
    state: Auth2States.AUTHENTICATED,
    previousState: state.state,
  })),
  on(auth2Actions.authenticateUserFailure, (state): State => ({
    ...state,
    authenticated: false,
    state: Auth2States.AUTHENTICATION_FAILED,
    previousState: state.state,
    currentUser: null,
  })),
  on(auth2Actions.getCurrentTenantUserSuccess, (state, { tenantUser}): State => ({
    ...state,
    currentTenantUser: tenantUser,
  })),
  on(auth2Actions.logout, (state): State => ({
    ...state,
    authenticated: false,
    state: Auth2States.LOGGED_OUT,
    previousState: state.state,
    currentUser: null,
    currentTenantUser: undefined,
    currentUserRoles: null,
  })),
  on(auth2Actions.listCurrentUserRolesSuccess, (state, { roles }): State => ({
    ...state,
    currentUserRoles: roles,
  })),
);

const selectAuth2State = createFeatureSelector<State>('auth2');

export const selectAuthenticated = createSelector(selectAuth2State, state => state.authenticated);

export const selectCurrentUserRoles = createSelector(selectAuth2State, state => state.currentUserRoles);

export const selectCurrentUserPrivileges = createSelector(selectCurrentUserRoles, (roles) => {
  if (!roles)
    return null;

  return roles.reduce((privileges, role) =>
    ([ ...privileges, ...role.privileges.map(p => p.name) ]), []);
});

export const selectRedirectUrl = (state: State) => state.redirectUrl;
export const selectPreviousState = (state: State) => state.previousState;
